/*
* Usage:
*
const { onUcIsInitialized, getUcServiceStatus, onUcConsentChange } = useUcConsentManager()

onMounted(() => {
    onUcIsInitialized(handleConsent)
})

function handleConsent () {
    const ucServiceName = 'Google Maps'
    const status = getUcServiceStatus(ucServiceName)

    // activate / deactivate service depending on initial status
    console.log(status)

    onUcConsentChange((e: any) => {
        if (e.detail && e.detail.event == 'consent_status') {
            // activate / deactivate service depending on consent changed
            console.log(e.detail[ucServiceName])
        }
    })
}
*
 */

import { useEventListener } from '@vueuse/core'

export interface IUseUcConsentManager {
    acceptUcServiceStatus(id: string): void,
    onUcIsInitialized (callback: () => void): void,
    getUcServiceStatus (name: string): boolean,
    onUcConsentChange (callback: (e: CustomEvent) => void): void
}

export function useUcConsentManager (): IUseUcConsentManager {
    const customUcEventName = 'ucEvent'

    function onUcIsInitialized (callback: () => void): void {
        if (window.UC_UI && window.UC_UI.isInitialized()) {
            callback()
        } else {
            // useEventListener for proper garbace collection to prevent memory leaks 
            useEventListener(window, 'UC_UI_INITIALIZED', () => callback())
        }
    }

    function acceptUcServiceStatus (id:string) {
        window.UC_UI?.acceptService(id)
    }

    function getUcServiceStatus (name: string): boolean {
        if (!window.UC_UI) return false

        const services = window.UC_UI?.getServicesBaseInfo()

        for (let i = 0; i < services.length; i++) {
            if (services[i]?.name === name) {
                return services[i]?.consent.status ?? false
            }
        }

        return false
    }

    // ucEvent is a custom window event, configured in:
    // Usercentrics Admin -> Implementation -> Data Layer & Events -> Window Event
    function onUcConsentChange (callback: (e: CustomEvent) => void): void {
        // useEventListener for proper garbace collection to prevent memory leaks 
        useEventListener(window, customUcEventName, (e: Event) => {
            callback(e as CustomEvent)
        })
    }

    return {
        acceptUcServiceStatus,
        onUcIsInitialized,
        getUcServiceStatus,
        onUcConsentChange
    }
}
